import z from 'zod';
import {zodTypeguard} from '../api/helpers';
import {
  RoleCountFieldDefinitionSchema,
  CrmFieldDefinitionSchema,
  AiFieldDefinitionSchema,
  isAiFieldDefinition,
  isResearchField,
  AiFieldType,
} from './enrichment';
import {IntentFieldDefinitionSchema} from './intent';

export const FieldDefinitionSchema = z.union([
  RoleCountFieldDefinitionSchema,
  CrmFieldDefinitionSchema,
  AiFieldDefinitionSchema,
  IntentFieldDefinitionSchema,
]);
export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;
export const isFieldDefinition = zodTypeguard(FieldDefinitionSchema);

export function isAiNonResearchField(fieldDefinition: FieldDefinition) {
  return (
    isAiFieldDefinition(fieldDefinition) && !isResearchField(fieldDefinition)
  );
}

// Restricted fields can't be used in scoring and have special export handling.
export function isRestrictedField(field: FieldDefinition) {
  return isResearchField(field);
}

export const FieldDefinitionTypeLabels: Record<
  FieldDefinition['type'],
  string
> = {
  ai: 'AI',
  crm: 'CRM',
  roleCount: 'Role Count',
  intent: 'Intent',
};

export function getFieldLabel(fieldDefinition?: FieldDefinition) {
  if (!fieldDefinition) {
    return 'Unknown Field';
  }

  if (!isAiFieldDefinition(fieldDefinition)) {
    return fieldDefinition.label;
  }

  const {fieldType} = fieldDefinition.config;
  // Since AI fields have the same label as the agent it belongs to, add the
  // field type to disambiguate.
  return `${fieldDefinition.label} (${AiFieldLabels[fieldType]})`;
}

export const AiFieldLabels = {
  boolean: 'Boolean',
  category: 'Category',
  estimate: 'Estimate',
  legacyResearch: 'Research',
  legacyTeamSize: 'Team Size',
  rating: 'Rating',
  research: 'Research',
  teamSize: 'Team Size',
} satisfies Record<AiFieldType, string>;
